import { useEffect, useState } from "react";

import { Formik } from "formik";

import {
  Avatar,
  Backdrop,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Modal,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { postNotification } from "../../../components/utilities/Notifications";
import ListResponsive from "../../../components/other/ListResponsive";

import { hasAccess } from "../EventHome";
import {
  ArmyListIconButton,
  armyListActions,
  getArmyListColor,
  userDisplayName
} from "./EventFunctions";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { BCPXmark } from "../../../components/icons/bcp/BCPIcons";
import { postData } from "../../../components/utilities/UtilityFunctions";
import { useTranslation } from "react-i18next";

export default function PlayerModal(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.PlayerModal"
  });
  const { t: tGeneral } = useTranslation();

  const {
    organize,
    event,
    isTeam,
    player,
    setPlayer,
    authContext,
    setRefreshPlayers,
    playerType,
    showOverall
  } = props;

  const [pairings, setPairings] = useState(null);
  const actions = armyListActions(player, event);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        setPairings(null);

        if (player) {
          if (event.status?.currentRound === 0) {
            setPairings([]);
          } else {
            try {
              await hasAccess(event, authContext, 3, false);

              var apiPairings = await handlePagingAll(
                "/pairings",
                isTeam
                  ? {
                      teamPlayerId: player.id,
                      pairingType: "TeamPairing",
                      "expand[]": [
                        "teamPlayer1",
                        "teamPlayer2",
                        "player1Game",
                        "player2Game"
                      ]
                    }
                  : {
                      playerId: player.id,
                      pairingType: "Pairing",
                      "expand[]": [
                        "player1",
                        "player2",
                        "player1Game",
                        "player2Game"
                      ]
                    }
              );

              apiPairings.sort((a, b) => {
                return a.round - b.round;
              });

              setPairings(apiPairings);
            } catch (err) {
              //
            }
          }
        }
      } catch (err) {
        postNotification("error", err);
      }
    };
    check();
    // eslint-disable-next-line
  }, [player]);

  const WrapperAvater = () => {
    if (organize) {
      return (
        <ButtonBase onClick={() => setManualOpen(true)}>
          <PlacingAvatar />
        </ButtonBase>
      );
    }
    return <PlacingAvatar />;
  };

  const PlacingAvatar = () => {
    const isManuallyPlaced = Boolean(
      player?.manualPlacing ||
        player?.manualHobbyPlacing ||
        player?.manualOverallPlacing
    );

    if (isManuallyPlaced) {
      return (
        <Tooltip title="Manually placed by the Organizer">
          <Avatar
            sx={{
              bgcolor: "secondary.main",
              color: "secondary.contrastText",
              width: 50,
              height: 50
            }}
          >
            #{(showOverall ? player?.overallPlacing : player?.placing) || "#"}
          </Avatar>
        </Tooltip>
      );
    }

    return (
      <Avatar
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          width: 50,
          height: 50
        }}
      >
        #{(showOverall ? player?.overallPlacing : player?.placing) || "#"}
      </Avatar>
    );
  };

  const MetricRow = ({ metric }) => {
    if (metric.modified) {
      return (
        <Tooltip
          componentsProps={{
            tooltip: { sx: { maxWidth: "90vw" } }
          }}
          title={
            <Box>
              <Typography variant="caption" sx={{ display: "block" }}>
                This metric has been modified by the organizer.
              </Typography>
            </Box>
          }
        >
          <Grid container spacing={1} sx={{ color: "warning.main" }}>
            <Grid item xs>
              {metric.name}
            </Grid>
            <Grid
              item
              xs
              sx={{
                textAlign: "right"
              }}
            >
              {metric.value}
            </Grid>
          </Grid>
        </Tooltip>
      );
    }
    return (
      <Grid container spacing={1}>
        <Grid item xs>
          {metric.name}
        </Grid>
        <Grid
          item
          xs
          sx={{
            textAlign: "right"
          }}
        >
          {metric.value}
        </Grid>
      </Grid>
    );
  };
  return (
    <>
      {player ? (
        <Modal
          disableAutoFocus
          open={Boolean(player)}
          onClose={() => {
            setPlayer(null);
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: ["95vw", "85vw", 600],
              minHeight: ["90vh", "95vh"],
              maxHeight: ["90vh", "95vh"],
              overflowY: "scroll",
              bgcolor: "background.secondary",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 24,
              p: 4
            }}
          >
            <IconButton
              aria-label="cancel"
              color="error"
              sx={{ position: "absolute", top: "4px", right: "4px" }}
              onClick={() => {
                setPlayer(null);
              }}
            >
              <BCPXmark />
            </IconButton>
            <Grid
              container
              spacing={2}
              sx={{
                pb: 1,
                mb: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
              }}
            >
              {(organize || !event.flags?.hidePlacings) && (
                <Grid item>
                  <WrapperAvater />
                </Grid>
              )}
              <Grid item xs>
                <Typography variant="h4" component="h2">
                  {player?.name ||
                    userDisplayName(
                      player?.firstName || player?.user?.firstName,
                      player?.lastName || player?.user?.lastName
                    )}
                </Typography>
                {player.team?.name && (
                  <Typography variant="h6" component="h2">
                    {player.team.name}
                  </Typography>
                )}
                {(player.army?.name || player?.faction?.name) && (
                  <Typography variant="h6" component="h2">
                    {player.army?.name || player?.faction?.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {!isTeam && (
              <Grid
                container
                spacing={2}
                sx={{
                  p: 3,
                  textAlign: "center"
                }}
              >
                <Grid item xs>
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: ["100%", "200px"],
                      bgcolor:
                        !organize && event.flags?.hideLists
                          ? "text.disabled"
                          : `${getArmyListColor(player)}.main`,
                      "&:hover": {
                        bgcolor:
                          !organize && event.flags?.hideLists
                            ? "text.disabled"
                            : `${getArmyListColor(player)}.dark`
                      },
                      color:
                        !organize && event.flags?.hideLists
                          ? "common.white"
                          : `${getArmyListColor(player)}.contrastText`
                    }}
                    {...actions}
                  >
                    {player?.armyListObjectId ||
                    player?.armyListId ||
                    player?.listId
                      ? t("viewList", {
                          listLabel: event?.gameSystem?.listLabel || "List"
                        })
                      : t("listUnsubmitted", {
                          listLabel: event?.gameSystem?.listLabel || "List"
                        })}
                  </Button>
                </Grid>
              </Grid>
            )}
            {(organize || !event.flags?.hidePlacings) &&
              event.eventMetrics &&
              (event.status?.numberOfRounds > 0 || !showOverall) && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      {player?.pod_metrics && player?.pod_games?.length ? (
                        <>
                          <Typography
                            variant="h4"
                            component="h2"
                            gutterBottom
                            sx={{ fontWeight: "700" }}
                          >
                            {event?.topCut && player?.podNum === 1
                              ? t("topCutMetrics")
                              : t("bracketMetrics")}
                          </Typography>

                          {player.pod_metrics
                            .filter((metric) => metric.name !== "Random")
                            .map((metric, index) => (
                              <Grid
                                item
                                xs={12}
                                key={index}
                                sx={{
                                  p: 1,
                                  border: "1px solid rgba(0, 0, 0, 0.12)",
                                  borderRadius: 1
                                }}
                              >
                                <MetricRow metric={metric} />
                              </Grid>
                            ))}
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{ fontWeight: "700" }}
                      >
                        {t("eventMetrics")}
                      </Typography>
                      {player?.total_metrics && (
                        <>
                          {player.total_metrics
                            .filter((metric) => metric.name !== "Random")
                            .map((metric, index) => (
                              <Grid
                                item
                                xs={12}
                                key={index}
                                sx={{
                                  p: 1,
                                  border: "1px solid rgba(0, 0, 0, 0.12)",
                                  borderRadius: 1
                                }}
                              >
                                <MetricRow metric={metric} />
                              </Grid>
                            ))}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}

            {showOverall && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: "700" }}
                  >
                    Soft Scores
                  </Typography>
                  {player?.softScores !== undefined &&
                    Object.values(player?.softScores).map(
                      (softScore, index) => {
                        if (softScore.scorecardId) {
                          return (
                            <Link
                              href={`/softscore/${softScore.id}/${player.id}`}
                              target="_blank"
                            >
                              <Typography
                                key={index}
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 0, textTransform: "uppercase" }}
                                paragraph
                              >
                                {softScore?.name} {softScore?.value}
                              </Typography>
                            </Link>
                          );
                        }
                        return (
                          <Typography
                            key={index}
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0, textTransform: "uppercase" }}
                            paragraph
                          >
                            {softScore?.name} {softScore?.value}
                          </Typography>
                        );
                      }
                    )}
                </Grid>
              </Grid>
            )}

            {!pairings ? (
              <Grid container spacing={2}>
                <Grid item xs={12} textAlign="center">
                  <CircularProgress />
                </Grid>
              </Grid>
            ) : event.status?.numberOfRounds > 0 || !showOverall ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: "700" }}
                  >
                    {t("pairings")}
                  </Typography>
                  {pairings.map((pairing, index) => {
                    return (
                      <ListResponsive
                        key={index}
                        originalList={[pairing]}
                        rowProps={{
                          mapFunction: (row, index) => {
                            return (
                              <Grid
                                item
                                xs={12}
                                key={index}
                                sx={{
                                  p: 1,
                                  //mb: 1,
                                  border: "1px solid rgba(0, 0, 0, 0.12)",
                                  borderRadius: 1
                                }}
                              >
                                <Grid container spacing={1}>
                                  <Grid item sx={{ width: "41.666667%" }}>
                                    {!isTeam && (
                                      <ArmyListIconButton
                                        event={event}
                                        player={row.player1}
                                        sx={{ mr: 1 }}
                                      />
                                    )}
                                    {row.player1
                                      ? row.player1.name ||
                                        userDisplayName(
                                          row.player1.firstName,
                                          row.player1.lastName
                                        )
                                      : row.teamPlayer1
                                        ? row.teamPlayer1.name
                                        : t("missingPlayerName")}
                                  </Grid>
                                  <Grid
                                    item
                                    sx={{
                                      width: "16.66%",
                                      textAlign: "center"
                                    }}
                                  >
                                    {row.table}
                                  </Grid>
                                  <Grid
                                    item
                                    sx={{
                                      width: "41.666667%",
                                      textAlign: "right"
                                    }}
                                  >
                                    {row.player2
                                      ? row.player2.name ||
                                        userDisplayName(
                                          row.player2.firstName,
                                          row.player2.lastName
                                        )
                                      : row.teamPlayer2
                                        ? row.teamPlayer2.name
                                        : t("missingPlayerName")}
                                    {!isTeam && (
                                      <ArmyListIconButton
                                        event={event}
                                        player={row.player2}
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            );
                          }
                        }}
                      />
                    );
                  })}
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
          </Box>
        </Modal>
      ) : (
        <></>
      )}

      {organize && manualOpen && (
        <ManualPlacingsModal
          player={player}
          setPlayer={setPlayer}
          manualOpen={manualOpen}
          setManualOpen={setManualOpen}
          setRefreshPlayers={setRefreshPlayers}
          playerType={playerType}
          t={t}
          tGeneral={tGeneral}
        />
      )}
    </>
  );
}

const ManualPlacingsModal = (props) => {
  const {
    manualOpen,
    setManualOpen,
    player,
    setPlayer,
    setRefreshPlayers,
    playerType,
    t,
    tGeneral
  } = props;
  const [processing, setProcessing] = useState(false);
  return (
    <>
      <Modal
        disableAutoFocus
        open={manualOpen}
        onClose={() => {
          setManualOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            minHeight: "95vh",
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setManualOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("manualPlacing")}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              placing: player?.manualPlacing,
              hobbyPlacing: player?.manualHobbyPlacing,
              overallPlacing: player?.manualOverallPlacing
            }}
            enableReinitialize
            onSubmit={async (values) => {
              var body = { set: {}, unset: {} };
              if (
                values.placing !== undefined &&
                values.placing !== "" &&
                values.placing !== 0
              ) {
                body.set.placing = values.placing;
              } else {
                body.unset.placing = true;
              }
              if (
                values.overallPlacing !== undefined &&
                values.overallPlacing !== "" &&
                values.overallPlacing !== 0
              ) {
                body.set.overallPlacing = values.overallPlacing;
              } else {
                body.unset.overallPlacing = true;
              }
              if (
                values.hobbyPlacing !== undefined &&
                values.hobbyPlacing !== "" &&
                values.hobbyPlacing !== 0
              ) {
                body.set.hobbyPlacing = values.hobbyPlacing;
              } else {
                body.unset.hobbyPlacing = true;
              }

              if (!Object.keys(body)?.length) {
                postNotification(
                  "warning",
                  tGeneral("notifications.warnings.noManualPlacingsProvided")
                );
                return;
              }

              setProcessing(true);
              try {
                await postData(
                  `/${
                    playerType === "teamplayers" ? "teamplayers" : "players"
                  }/${player?.id}/setPlacing`,
                  {
                    body: body
                  }
                );
                postNotification(
                  "success",
                  tGeneral("notifications.success.playerPlacingsSet")
                );
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
                return;
              }
              setRefreshPlayers(true);
              setPlayer(null);
              setManualOpen(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="placing"
                      label="Placing"
                      value={values.placing}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.placing && errors.placing)}
                      helperText={touched.placing && errors.placing}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="overallPlacing"
                      label="Overall Placing"
                      value={values.overallPlacing}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.overallPlacing && errors.overallPlacing
                      )}
                      helperText={
                        touched.overallPlacing && errors.overallPlacing
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="hobbyPlacing"
                      label="Hobby Placing"
                      value={values.hobbyPlacing}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.hobbyPlacing && errors.hobbyPlacing
                      )}
                      helperText={touched.hobbyPlacing && errors.hobbyPlacing}
                    />
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => setManualOpen(false)}
                    >
                      {t("cancel")}
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      {t("update")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>

          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mt: 2,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("manualSeed")}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              seed: player?.bracket_seed
            }}
            enableReinitialize
            onSubmit={async (values) => {
              var body = { set: {}, unset: {} };
              if (
                values.seed !== undefined &&
                values.seed !== "" &&
                values.seed !== 0
              ) {
                body.set.seed = values.seed;
              } else {
                body.unset.seed = true;
              }

              setProcessing(true);
              try {
                await postData(`/players/${player?.id}/setSeed`, {
                  body: body
                });
                postNotification(
                  "success",
                  tGeneral("notifications.success.playerSeedSet")
                );
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
                return;
              }
              setRefreshPlayers(true);
              setPlayer(null);
              setManualOpen(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="seed"
                      label="Seed"
                      value={values.seed}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.seed && errors.seed)}
                      helperText={touched.seed && errors.seed}
                    />
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => setManualOpen(false)}
                    >
                      {t("cancel")}
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      {t("update")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>

          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mt: 2,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("groupInfo")}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              groupID: player?.groupID,
              countryCode: player?.countryCode,
              groupName: player?.groupName
            }}
            enableReinitialize
            onSubmit={async (values) => {
              var body = { set: {}, unset: {} };
              if (
                values.groupID !== undefined &&
                values.groupID !== "" &&
                values.groupID !== 0
              ) {
                body.set.groupID = values.groupID;
              } else {
                body.unset.groupID = true;
              }

              if (
                values.countryCode !== undefined &&
                values.countryCode !== "" &&
                values.countryCode !== 0
              ) {
                body.set.countryCode = values.countryCode;
              } else {
                body.unset.countryCode = true;
              }

              if (
                values.groupName !== undefined &&
                values.groupName !== "" &&
                values.groupName !== 0
              ) {
                body.set.groupName = values.groupName;
              } else {
                body.unset.groupName = true;
              }

              setProcessing(true);
              try {
                await postData(`/players/${player?.id}/setGroupInfo`, {
                  body: body
                });
                postNotification(
                  "success",
                  tGeneral("notifications.success.playerGroupInfoSet")
                );
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
                return;
              }
              setRefreshPlayers(true);
              setPlayer(null);
              setManualOpen(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="groupID"
                      label="Group ID"
                      value={values.groupID}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.groupID && errors.groupID)}
                      helperText={touched.groupID && errors.groupID}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="countryCode"
                      label="Country Code"
                      value={values.countryCode}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.countryCode && errors.countryCode)}
                      helperText={touched.countryCode && errors.countryCode}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="groupName"
                      label="Group Name / Country Name"
                      value={values.groupName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.groupName && errors.groupName)}
                      helperText={touched.groupName && errors.groupName}
                    />
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => setManualOpen(false)}
                    >
                      {t("cancel")}
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      {t("update")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
      {processing && <ModalLoadingFull />}
    </>
  );
};
